import { useState, useCallback } from 'react';
import { Button, Input, Tabs, TabList, Tab, TabPanel } from '@kollektive/design-system';
import { Plus, Search, X } from 'lucide-react';
import { useTeam, type TeamMember } from '@/hooks/useTeam';
import { useInvitations, type Invitation } from '@/hooks/useInvitations';
import { useAuth } from '@/hooks/useAuth';
import {
  MemberTable,
  EditMemberModal,
  RemoveMemberDialog,
  CancelInvitationDialog,
  InvitationsTable,
} from '@/components/team';
import { InviteModal } from '@/components/invitations';
import styles from './TeamPage.module.css';

export function TeamPage() {
  const { user } = useAuth();
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
    refresh: refreshMembers,
    updateMember,
    removeMember,
    revealMember,
    isUpdating,
    isRemoving,
  } = useTeam();

  const {
    invitations,
    isLoading: invitationsLoading,
    error: invitationsError,
    refresh: refreshInvitations,
    createInvitation,
    cancelInvitation,
    resendInvitation,
    revealInvitation,
    isCreating,
    isCancelling,
    isResending,
  } = useInvitations();

  // UI state
  const [activeTab, setActiveTab] = useState('members');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState<TeamMember | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<TeamMember | null>(null);
  const [membersToRemove, setMembersToRemove] = useState<TeamMember[]>([]);
  const [invitationToCancel, setInvitationToCancel] = useState<Invitation | null>(null);
  const [invitationsToCancel, setInvitationsToCancel] = useState<Invitation[]>([]);

  // Current user info - get role from members list since Supabase auth doesn't have it
  const currentUserId = user?.id || '';
  const currentUser = members.find((m) => m.id === currentUserId);
  const currentUserRole = currentUser?.role || 'viewer';

  // Handlers for members
  const handleEditMember = useCallback((member: TeamMember) => {
    setMemberToEdit(member);
  }, []);

  const handleSaveEdit = useCallback(async (memberId: string, role: string) => {
    await updateMember(memberId, { role });
  }, [updateMember]);

  const handleRemoveMember = useCallback((member: TeamMember) => {
    setMemberToRemove(member);
  }, []);

  const handleBulkRemove = useCallback((members: TeamMember[]) => {
    setMembersToRemove(members);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (memberToRemove) {
      await removeMember(memberToRemove.id);
      setMemberToRemove(null);
    } else if (membersToRemove.length > 0) {
      for (const member of membersToRemove) {
        await removeMember(member.id);
      }
      setMembersToRemove([]);
    }
  }, [memberToRemove, membersToRemove, removeMember]);

  const handleRemoveCancel = useCallback(() => {
    setMemberToRemove(null);
    setMembersToRemove([]);
  }, []);

  // Handlers for invitations
  const handleInvite = useCallback(async (email: string, role: string) => {
    await createInvitation({ email, role });
    setShowInviteModal(false);
  }, [createInvitation]);

  const handleCancelInvitation = useCallback((invitation: Invitation) => {
    setInvitationToCancel(invitation);
  }, []);

  const handleResendInvitation = useCallback(async (invitation: Invitation) => {
    await resendInvitation(invitation.id);
  }, [resendInvitation]);

  const handleBulkCancelInvitations = useCallback((invitations: Invitation[]) => {
    setInvitationsToCancel(invitations);
  }, []);

  const handleCancelInvitationConfirm = useCallback(async () => {
    if (invitationToCancel) {
      await cancelInvitation(invitationToCancel.id);
      setInvitationToCancel(null);
    } else if (invitationsToCancel.length > 0) {
      for (const inv of invitationsToCancel) {
        await cancelInvitation(inv.id);
      }
      setInvitationsToCancel([]);
    }
  }, [invitationToCancel, invitationsToCancel, cancelInvitation]);

  // Determine which remove dialog to show
  const showRemoveDialog = memberToRemove !== null || membersToRemove.length > 0;
  const removeDialogMember = memberToRemove ?? membersToRemove[0];
  const isBulkRemove = membersToRemove.length > 0;

  // Check if current user can invite (owner or admin only)
  const canInvite = currentUserRole === 'owner' || currentUserRole === 'admin';

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Team</h1>
          <p className={styles.subtitle}>
            Manage your workspace members and invitations
          </p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.searchWrapper}>
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
              iconLeft={<Search size={16} />}
              iconRight={
                searchQuery ? (
                  <button
                    className={styles.clearButton}
                    onClick={() => setSearchQuery('')}
                    type="button"
                  >
                    <X size={14} />
                  </button>
                ) : undefined
              }
              className={styles.searchInput}
            />
          </div>
          {canInvite && (
            <Button
              variant="primary"
              onClick={() => setShowInviteModal(true)}
              title="Invite Member"
              className={styles.iconButton}
            >
              <Plus size={20} />
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <TabList>
          <Tab value="members">
            Members {members.length > 0 && `(${members.length})`}
          </Tab>
          <Tab value="invitations">
            Pending Invitations {invitations.length > 0 && `(${invitations.length})`}
          </Tab>
        </TabList>

        <TabPanel value="members" className={styles.tabContent}>
          <MemberTable
            members={members}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            isLoading={membersLoading}
            error={membersError}
            onRetry={refreshMembers}
            onEdit={handleEditMember}
            onRemove={handleRemoveMember}
            onBulkRemove={handleBulkRemove}
            onReveal={revealMember}
            isRemoving={isRemoving}
            searchQuery={searchQuery}
          />
        </TabPanel>

        <TabPanel value="invitations" className={styles.tabContent}>
          <InvitationsTable
            invitations={invitations}
            currentUserId={currentUserId}
            isLoading={invitationsLoading}
            error={invitationsError}
            onRetry={refreshInvitations}
            onCancel={handleCancelInvitation}
            onResend={handleResendInvitation}
            onBulkCancel={handleBulkCancelInvitations}
            onReveal={revealInvitation}
            isCancelling={isCancelling}
            isResending={isResending}
            searchQuery={searchQuery}
          />
        </TabPanel>
      </Tabs>

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        isLoading={isCreating}
      />

      {/* Edit Member Modal */}
      <EditMemberModal
        isOpen={memberToEdit !== null}
        onClose={() => setMemberToEdit(null)}
        member={memberToEdit}
        currentUserRole={currentUserRole}
        onSave={handleSaveEdit}
        isLoading={isUpdating}
      />

      {/* Remove Member Dialog */}
      {showRemoveDialog && removeDialogMember && (
        <RemoveMemberDialog
          member={removeDialogMember}
          memberCount={isBulkRemove ? membersToRemove.length : 1}
          isOpen={true}
          onClose={handleRemoveCancel}
          onConfirm={handleRemoveConfirm}
          isLoading={isRemoving}
        />
      )}

      {/* Cancel Invitation Dialog */}
      {(invitationToCancel !== null || invitationsToCancel.length > 0) && (
        <CancelInvitationDialog
          invitation={(invitationToCancel ?? invitationsToCancel[0])!}
          invitationCount={invitationsToCancel.length > 0 ? invitationsToCancel.length : 1}
          isOpen={true}
          onClose={() => {
            setInvitationToCancel(null);
            setInvitationsToCancel([]);
          }}
          onConfirm={handleCancelInvitationConfirm}
          isLoading={isCancelling}
        />
      )}
    </div>
  );
}
